/*
  Global Tools — interacciones compartidas (sin librerías).

  1. Selector de cantidad (snippets/gt-qty.liquid): − y + suman y restan,
     mantener pulsado repite, al tocar el mínimo o el máximo el control se
     sacude. En el carrito, el cambio se guarda solo.
  2. Botón del carrito (gt-button kind 'cart'): agrega por /cart/add.js sin
     salir de la página, se hunde, muestra un indicador de carga y pasa a
     "Agregado". Sin JavaScript el formulario se envía de forma nativa.

  Idea tomada de los componentes de 21st.dev «Quantity Stepper» y «Add to
  Cart Button», reescrita en JS propio. Las animaciones viven en
  gt-design-system.css y respetan prefers-reduced-motion.
*/
(() => {
  const flash = (el, cls) => {
    el.classList.remove(cls);
    void el.offsetWidth; // reinicia la animación si se repite seguida
    el.classList.add(cls);
    el.addEventListener('animationend', () => el.classList.remove(cls), { once: true });
  };

  /* ---------- 1. Selector de cantidad ---------- */

  const limits = (input) => ({
    min: input.min === '' ? 1 : Number(input.min),
    max: input.max === '' ? Infinity : Number(input.max),
  });

  const sync = (root) => {
    const input = root.querySelector('.gt-qty__input');
    if (!input) return;
    const { min, max } = limits(input);
    const value = Number(input.value);
    root.querySelector('[data-gt-qty-step="-1"]').disabled = value <= min;
    root.querySelector('[data-gt-qty-step="1"]').disabled = value >= max;
  };

  const autosubmit = (root) => {
    if (!root.hasAttribute('data-gt-qty-autosubmit')) return;
    clearTimeout(root._gtSubmit);
    root._gtSubmit = setTimeout(() => {
      const input = root.querySelector('.gt-qty__input');
      const form = input.form;
      if (!form) return;
      const update = form.querySelector('[name="update"]');
      if (form.requestSubmit) form.requestSubmit(update || undefined);
      else form.submit();
    }, 600);
  };

  const step = (button) => {
    const root = button.closest('[data-gt-qty]');
    const input = root && root.querySelector('.gt-qty__input');
    if (!input) return false;
    const { min, max } = limits(input);
    const current = Math.floor(Number(input.value)) || min;
    const next = current + Number(button.dataset.gtQtyStep);
    if (next < min || next > max) {
      flash(root, 'is-limit');
      return false;
    }
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    flash(root, 'is-bump');
    return true;
  };

  // Mantener pulsado: primer paso inmediato, luego repite tras 400 ms.
  let holdTimer = null;
  let holdInterval = null;
  let pointerStepAt = 0;
  const stopHold = () => {
    clearTimeout(holdTimer);
    clearInterval(holdInterval);
    holdTimer = holdInterval = null;
  };

  document.addEventListener('pointerdown', (event) => {
    const button = event.target.closest('[data-gt-qty-step]');
    if (!button || button.disabled || event.button !== 0) return;
    pointerStepAt = Date.now();
    step(button);
    holdTimer = setTimeout(() => {
      holdInterval = setInterval(() => {
        pointerStepAt = Date.now();
        if (button.disabled || !step(button)) stopHold();
      }, 90);
    }, 400);
  });
  ['pointerup', 'pointercancel', 'pointerleave', 'blur'].forEach((type) => {
    document.addEventListener(type, stopHold, true);
  });

  // Teclado (Enter o Espacio) llega como click sin pointerdown previo; el
  // click que sigue a un toque ya se contó en pointerdown.
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-gt-qty-step]');
    if (!button || Date.now() - pointerStepAt < 1000) return;
    step(button);
  });

  // Lo escrito a mano se normaliza: entero dentro de los límites.
  document.addEventListener('change', (event) => {
    const input = event.target.closest('.gt-qty__input');
    if (!input) return;
    const root = input.closest('[data-gt-qty]');
    const { min, max } = limits(input);
    let value = Math.floor(Number(input.value));
    if (Number.isNaN(value)) value = min;
    value = Math.min(Math.max(value, min), max);
    if (String(value) !== input.value) input.value = value;
    sync(root);
    autosubmit(root);
  });

  document.querySelectorAll('[data-gt-qty]').forEach(sync);

  /* ---------- 2. Agregar al carrito sin salir de la página ---------- */

  const live = document.createElement('p');
  live.className = 'gt-visually-hidden';
  live.setAttribute('aria-live', 'polite');
  document.body.appendChild(live);

  const setState = (button, state, text) => {
    const label = button.querySelector('[data-gt-label]');
    if (label && !button.dataset.gtLabel) button.dataset.gtLabel = label.textContent;
    button.dataset.state = state;
    if (label) label.textContent = text || button.dataset.gtLabel;
  };

  const updateCounter = (count) => {
    document.querySelectorAll('[data-cart-counter]').forEach((counter) => {
      const number = counter.querySelector('[data-cart-count]');
      if (number) number.textContent = count > 0 ? count : '';
      counter.classList.toggle('is-active', count > 0);
      flash(counter, 'is-bump');
    });
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    const button = event.submitter && event.submitter.matches('[data-gt-cart-btn]')
      ? event.submitter
      : form.querySelector('[data-gt-cart-btn]');
    if (!button || !window.fetch || !/\/cart\/add/.test(form.action)) return;

    event.preventDefault();
    if (button.dataset.state === 'loading') return;
    setState(button, 'loading', 'Agregando…');

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'No se pudo agregar');

      setState(button, 'added', 'Agregado al carrito');
      live.textContent = `${data.product_title || 'Producto'} agregado al carrito.`;
      const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json());
      updateCounter(cart.item_count);
    } catch (error) {
      setState(button, 'error', 'No se pudo agregar');
      live.textContent = error.message;
    }

    setTimeout(() => setState(button, 'idle'), 2200);
  });
})();
